/*
 * sunset-delivery-zones.js
 * Sunset Landscape Supply — ZIP-code delivery fee lookup (Florida build).
 *
 * Single market: FL — Florida Panhandle, dispatched from the Pensacola
 * yard (3846 N Davis Hwy #2, Pensacola, FL 32503).
 *
 * Pricing rule:
 *   fee = flatFee + max(0, miles - freeMiles) * perMile, rounded to the
 *   nearest whole dollar. The fee is charged PER LOAD (dump-truck trip),
 *   matching cart + product-page logic.
 *
 * Data: ZIPs across South Escambia & South Santa Rosa counties, FL
 * (Pensacola yard) — incl. PO-box ZIPs mapped to their parent areas.
 * Driving miles are ESTIMATES that must be verified before launch;
 * straight-line miles are placeholders derived as round(driving * 0.8)
 * until real great-circle numbers exist.
 *
 * Public API (window.SunsetDelivery):
 *   .lookup(zip)            -> null | {zip, town, place, county, market,
 *                                      marketLabel, origin, label, miles,
 *                                      drivingMiles, straightMiles, fee,
 *                                      inFlatZone}
 *   .quote(zip, loads)      -> {zip, town, miles, feePerLoad, loads, total,
 *                               inFlatZone, known, market, marketLabel,
 *                               inLouisiana, inServiceArea, label}
 *   .format(zip)            -> short user-facing sentence for the current state
 *   .feeForMiles(miles, market) -> number (market optional, defaults to 'LA')
 *   .getZip() / .setZip(z) / .clearZip()
 *   .isLouisiana(zip)   -> boolean. LEGACY NAME, new meaning: "this ZIP
 *                              is in our service table". Kept so existing
 *                              call sites do not break; prefer the alias
 *                              .isInServiceArea(zip).
 *   .isInServiceArea(zip)   -> boolean (alias of the above)
 *   .normalize(zip)         -> '32503' | null
 *   .MARKETS                -> per-market rate config
 *   .FLAT_FEE (100) .FREE_MILES (25) .PER_MILE (4)
 *                              legacy aliases (= FL rates)
 *   .basis ('driving')
 *   .COOKIE ('sunset_zip')
 *
 * Events (on document):
 *   'sunset:ready'      { detail: { zip, quote } }  fired once, after init
 *   'sunset:zip-change' { detail: { zip, quote } }  fired on every setZip/clearZip,
 *                                                 and once at init if a ZIP is stored
 *
 * Storage: plain cookie `sunset_zip`, 30-day expiry, path=/, SameSite=Lax.
 * (localStorage is deliberately NOT the primary store — see PATCHES.md.)
 *
 * NOTE: the Sunset* identifier / cookie / event names are intentionally
 * unchanged here; a separate global rename pass handles those.
 */
(function (w, d) {
  'use strict';
  if (w.SunsetDelivery) return; // idempotent: safe if the asset is included twice

  /* ------------------------------------------------------------------ *
   * MARKET CONFIG (single market: Pensacola, FL)
   * // TWEAK RATES HERE
   * ------------------------------------------------------------------ */
  var MARKETS = {
    FL: {
      label: 'Florida — Pensacola',
      origin: 'Pensacola yard',
      flatFee: 100,   // dollars per trip, covers everything inside freeMiles
      freeMiles: 25,  // miles included in the per-trip base
      perMile: 4,     // dollars per mile beyond freeMiles
      // NOTE (v2, ton/cy split): trip capacity is no longer a single
      // cubic-yard number — stone & gravel run 4 TONS per trip, sand &
      // soil run 4 CUBIC YARDS per trip. Both live as their own constants
      // in snippets/buy-buttons.liquid (sunset_truck_capacity) and
      // sections/price-sheet.liquid (ps_ypl), since capacity is the same
      // number either way and only the unit label differs there. This
      // field is not read by any current code path; kept only so it
      // doesn't read as a stale "3 yd" claim if someone finds it later.
      capacityPerTrip: 4 // tons (stone & gravel) or cubic yards (sand & soil) — see note above
    }
  };

  /* ------------------------------------------------------------------ *
   * BASIS SWITCH — change this ONE line to swap the distance basis.
   *   'driving'  = road miles (correct for a dump truck)
   *   'straight' = great-circle miles. Currently a placeholder estimated
   *                as round(driving * 0.8); replace with real numbers
   *                before ever switching the basis.
   * ------------------------------------------------------------------ */
  var BASIS = 'driving';

  /* Legacy single-market constants, kept as fallback aliases so nothing
     that still reads them breaks. They mirror the FL (only) market. */
  var FLAT_FEE = MARKETS.FL.flatFee;
  var FREE_MILES = MARKETS.FL.freeMiles;
  var PER_MILE = MARKETS.FL.perMile;
  var COOKIE = 'sunset_zip';
  var COOKIE_DAYS = 30;

  /* Row layout: [drivingMiles, straightMiles, town, county, market, place?]
     The market code doubles as the state abbreviation (FL). */
  var MILES_INDEX = BASIS === 'straight' ? 1 : 0;

  // ESTIMATED driving miles — verify before launch.
  // straightMiles = round(drivingMiles * 0.8) placeholder, same caveat.
  var ZONES = {
/* --- Escambia County, FL — dispatched from the Pensacola yard (3846 N Davis Hwy) --- */
"32501":[4,3,"Pensacola","Escambia County","FL"],
"32502":[5,4,"Pensacola","Escambia County","FL"],
"32503":[2,2,"Pensacola","Escambia County","FL"],
"32504":[3,2,"Pensacola","Escambia County","FL"],
"32505":[5,4,"Pensacola","Escambia County","FL"],
"32506":[9,7,"West Pensacola","Escambia County","FL"],
"32507":[12,10,"Warrington","Escambia County","FL"],
"32508":[13,10,"NAS Pensacola","Escambia County","FL"],
"32509":[7,6,"Pensacola","Escambia County","FL"],
"32511":[9,7,"Pensacola","Escambia County","FL"],
"32512":[13,10,"Pensacola","Escambia County","FL"],
"32513":[4,3,"Pensacola","Escambia County","FL"],
"32514":[4,3,"Ferry Pass","Escambia County","FL"],
"32516":[5,4,"Pensacola","Escambia County","FL"],
"32520":[5,4,"Pensacola","Escambia County","FL"],
"32521":[5,4,"Pensacola","Escambia County","FL"],
"32522":[5,4,"Pensacola","Escambia County","FL"],
"32523":[5,4,"Pensacola","Escambia County","FL"],
"32524":[5,4,"Pensacola","Escambia County","FL"],
"32526":[10,8,"Bellview","Escambia County","FL"],
"32533":[10,8,"Cantonment","Escambia County","FL"],
"32534":[6,5,"Ensley","Escambia County","FL"],
"32559":[13,10,"NAS Pensacola","Escambia County","FL"],
/* --- Santa Rosa County, FL (32561 also covers Pensacola Beach, Escambia) --- */
"32561":[10,8,"Gulf Breeze","Santa Rosa County","FL"],
"32562":[10,8,"Gulf Breeze","Santa Rosa County","FL"],
"32563":[15,12,"Gulf Breeze","Santa Rosa County","FL"],
"32566":[26,21,"Navarre","Santa Rosa County","FL"],
"32570":[22,18,"Milton","Santa Rosa County","FL"],
"32571":[14,11,"Pace","Santa Rosa County","FL"],
"32572":[22,18,"Milton","Santa Rosa County","FL"],
"32583":[23,18,"East Milton","Santa Rosa County","FL"],
"32530":[21,17,"Bagdad","Santa Rosa County","FL"]
};

  /* ---------------------------- helpers ---------------------------- */

  function normalize(zip) {
    if (zip === 0 || zip) {
      var s = String(zip).replace(/[^0-9]/g, '');
      if (s.length >= 5) return s.slice(0, 5);
      if (s.length > 0 && s.length < 5) return null;
    }
    return null;
  }

  /* LEGACY NAME, new meaning. Historically "is this an in-state ZIP";
     this store serves the Pensacola, FL market, so this answers "is this
     ZIP in our service table". The name is kept so existing call sites
     keep working — `isInServiceArea` is the preferred alias going forward. */
  function isLouisiana(zip) {
    var z = normalize(zip);
    if (!z) return false;
    return Object.prototype.hasOwnProperty.call(ZONES, z);
  }

  /* Fee for a mileage. `market` is optional and defaults to 'FL'
     (the only market). */
  function feeForMiles(miles, market) {
    var mk = (market && MARKETS[market]) || MARKETS.FL;
    var m = Number(miles);
    if (!isFinite(m) || m < 0) m = 0;
    var over = Math.max(0, m - mk.freeMiles);
    return Math.round(mk.flatFee + over * mk.perMile);
  }

  function money(n) {
    return '$' + Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function money0(n) {
    return '$' + Math.round(Number(n)).toLocaleString('en-US');
  }

  /* ----------------------------- lookup ---------------------------- */

  function lookup(zip) {
    var z = normalize(zip);
    if (!z) return null;
    var row = Object.prototype.hasOwnProperty.call(ZONES, z) ? ZONES[z] : null;
    if (!row) return null;
    var miles = row[MILES_INDEX];
    var town = row[2];
    var county = row[3];
    var market = row[4] || 'FL';
    var place = row[5] || '';
    var mk = MARKETS[market] || MARKETS.FL;
    return {
      zip: z,
      town: town,
      place: place,
      county: county,
      market: market,
      marketLabel: mk.label,
      origin: mk.origin,
      label: place && place !== town ? place + ', ' + market : town + ', ' + market,
      miles: miles,
      drivingMiles: row[0],
      straightMiles: row[1],
      fee: feeForMiles(miles, market),
      inFlatZone: miles <= mk.freeMiles
    };
  }

  /* ----------------------------- quote ----------------------------- *
   * `loads` is the number of dump-truck trips. The delivery fee is
   * charged PER LOAD — identical to the existing cart maths
   * (loads * DELIVERY_FEE). All rates come from the Pensacola yard.
   * Unknown ZIPs fall back to the flat fee so nothing renders as
   * NaN, but `known:false` means the UI MUST say the fee is confirmed
   * at checkout instead of presenting the number as final.
   * ------------------------------------------------------------------ */
  function quote(zip, loads) {
    var n = parseInt(loads, 10);
    if (!isFinite(n) || n < 1) n = 1;
    var hit = lookup(zip);
    var z = normalize(zip);
    if (!hit) {
      var inArea = isLouisiana(z); // legacy fn name = "in service table"
      return {
        zip: z,
        town: null,
        label: null,
        miles: null,
        market: null,
        marketLabel: null,
        feePerLoad: FLAT_FEE,
        loads: n,
        total: FLAT_FEE * n,
        inFlatZone: false,
        known: false,
        inLouisiana: inArea, // legacy field name, kept for old consumers
        inServiceArea: inArea
      };
    }
    return {
      zip: hit.zip,
      town: hit.town,
      label: hit.label,
      miles: hit.miles,
      market: hit.market,
      marketLabel: hit.marketLabel,
      feePerLoad: hit.fee,
      loads: n,
      total: hit.fee * n,
      inFlatZone: hit.inFlatZone,
      known: true,
      inLouisiana: true, // legacy field name, kept for old consumers
      inServiceArea: true
    };
  }

  /* ---------------------------- messaging -------------------------- */

  function format(zip) {
    var z = normalize(zip);
    if (!z) return 'Enter your delivery ZIP for an exact delivery price.';
    var q = quote(z, 1);
    if (q.known && q.inFlatZone) {
      return 'Delivering to ' + q.label + ' — ' + money0(q.feePerLoad) + ' flat per load.';
    }
    if (q.known) {
      var origin = (MARKETS[q.market] || MARKETS.FL).origin;
      return 'Delivering to ' + q.label + ' — ' + money0(q.feePerLoad) +
        ' per load (' + q.miles + ' mi from our ' + origin + ').';
    }
    if (!q.inServiceArea) {
      return 'That ZIP looks outside our Pensacola service area — text (850) 462-3749 and we will confirm if we can reach you.';
    }
    return 'We deliver to ' + z + ' — your exact delivery price is confirmed at checkout.';
  }

  /* ----------------------------- cookie ---------------------------- */

  function readCookie(name) {
    try {
      var parts = ('; ' + d.cookie).split('; ' + name + '=');
      if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    } catch (e) {}
    return null;
  }

  function writeCookie(name, value, days) {
    try {
      var exp = new Date(Date.now() + days * 864e5).toUTCString();
      d.cookie = name + '=' + encodeURIComponent(value) +
        '; expires=' + exp + '; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function eraseCookie(name) {
    try {
      d.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function getZip() {
    return normalize(readCookie(COOKIE));
  }

  function emit(name, zip) {
    try {
      d.dispatchEvent(new CustomEvent(name, {
        detail: { zip: zip, quote: zip ? quote(zip, 1) : null }
      }));
    } catch (e) {
      /* IE-era fallback; harmless everywhere else */
      try {
        var ev = d.createEvent('CustomEvent');
        ev.initCustomEvent(name, true, false, { zip: zip, quote: zip ? quote(zip, 1) : null });
        d.dispatchEvent(ev);
      } catch (e2) {}
    }
  }

  function setZip(zip) {
    var z = normalize(zip);
    if (!z) return null;
    if (z === getZip()) { emit('sunset:zip-change', z); return z; } // still re-notify listeners
    writeCookie(COOKIE, z, COOKIE_DAYS);
    /* Mirror into localStorage as a best-effort convenience only.
       The cookie is the source of truth; a storage failure is ignored. */
    try { w.localStorage.setItem(COOKIE, z); } catch (e) {}
    emit('sunset:zip-change', z);
    return z;
  }

  function clearZip() {
    eraseCookie(COOKIE);
    try { w.localStorage.removeItem(COOKIE); } catch (e) {}
    emit('sunset:zip-change', null);
  }

  /* ------------------------------ API ------------------------------ */

  w.SunsetDelivery = {
    lookup: lookup,
    quote: quote,
    format: format,
    feeForMiles: feeForMiles,
    normalize: normalize,
    isLouisiana: isLouisiana, // legacy name — means "in service table"
    isInServiceArea: isLouisiana, // preferred alias, same function
    getZip: getZip,
    setZip: setZip,
    clearZip: clearZip,
    money: money,
    money0: money0,
    MARKETS: MARKETS,
    FLAT_FEE: FLAT_FEE,
    FREE_MILES: FREE_MILES,
    PER_MILE: PER_MILE,
    basis: BASIS,
    COOKIE: COOKIE,
    size: 0,
    zones: ZONES
  };

  try { w.SunsetDelivery.size = Object.keys(ZONES).length; } catch (e) {}

  /* Announce ourselves. Any consumer that rendered before this asset
     finished loading gets a chance to re-render. */
  var saved = getZip();
  emit('sunset:ready', saved);
  if (saved) emit('sunset:zip-change', saved);
})(window, document);
